import { Link } from '@fs/zion-router'
import { LogoFamilysearch } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getEntity } from '../../util/EntityUtil'
import { isNotNullOrUndefinedOrEmpty, isNullOrUndefinedOrEmpty } from '../../util/Utils'
import { getReservationPersonIds } from '../../services/reservationsService'
import useNotifications from '../../hooks/useNotifications'
import { createAncestryUrl, getFrontendBaseUrl } from '../../util/UrlUtil'
import { getIdElement } from '../../util/StringUtil'
import { openAncestryTab } from '../queues/useQueues'
import ancestryIcon from '../../assets/ancestry.svg'

const hiddenCss = css`
  background-color: #ffff00;
`
const getLinkTarget = (newTab) => {
  switch (newTab) {
    case true:
      return '_blank'
    case false:
    case null:
    case undefined:
      return '_self'
    default:
      return newTab
  }
}

export function PersonLink({
  personId,
  personName,
  showId = false,
  showDecodedId = false,
  forward = true,
  newTab = true,
  ...props
}) {
  let linkText = personId
  if (isNotNullOrUndefinedOrEmpty(personName)) {
    linkText = personName
  }
  if (showId === true) {
    linkText = `${personName || '?'} (${personId})`
  }

  if (showDecodedId) {
    const decodedId = getEntity(personId, 7).numericId
    linkText = `${linkText} (${decodedId})`
  }

  return (
    <Link
      id={`Person Link - ${linkText}`}
      to={`/person/${personId}/summary?forward=${forward}`}
      target={getLinkTarget(newTab)}
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {linkText}
    </Link>
  )
}

export function TreePersonLink({
  personId,
  personName,
  showId = false,
  showDecodedId = false,
  newTab = true,
  treeIcon = false,
  memories = false,
  ...props
}) {
  if (!personId) {
    return null
  }
  let linkText = personId
  if (isNotNullOrUndefinedOrEmpty(personName)) {
    linkText = personName
  }
  if (showId === true) {
    linkText = `${personName || '?'} (${personId})`
  }

  if (showDecodedId) {
    const decodedId = getEntity(personId, 7).numericId
    linkText = `${linkText} (${decodedId})`
  }

  return (
    <Link
      id={`Tree Person Link - ${linkText}`}
      to={`${getFrontendBaseUrl(true)}/tree/person/${memories ? 'memories' : 'details'}/${personId}`}
      target={getLinkTarget(newTab)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {treeIcon ? <LogoFamilysearch /> : linkText}
    </Link>
  )
}

PersonLink.propTypes = {
  personId: PropTypes.string,
  personName: PropTypes.string,
}

// Shared color for the AncestryPersonLink icon and any TreePersonLink icon rendered next to it,
// so the pair reads as a matched set instead of the tree icon's default link-purple.
export const treeAncestryIconColor = '#222222'

// The source asset is the full-color Ancestry logo, not a currentColor-aware icon, so it's
// recolored via CSS mask to match treeAncestryIconColor.
const ancestryIconCss = css`
  display: inline-block;
  width: 22px;
  height: 18px;
  vertical-align: middle;
  background-color: ${treeAncestryIconColor};
  -webkit-mask-image: url(${ancestryIcon});
  mask-image: url(${ancestryIcon});
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-position: center;
  mask-position: center;
`

export function AncestryPersonLink({ family, person, ...props }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  return (
    <Link
      id="Ancestry Person Link"
      data-testid="family-details-ancestry-link"
      to={createAncestryUrl(family, person)}
      rel="noreferrer"
      aria-label={t('queues.ancestrySearch', 'Search on Ancestry')}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        const result = openAncestryTab(family, person)
        if (result.error) {
          error(result.msg)
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      <span css={ancestryIconCss} data-testid="ancestry-icon" aria-hidden="true" />
    </Link>
  )
}

export function RelationshipLink({ relationshipId, relationshipType, newTab = true, ...props }) {
  return (
    <Link
      to={`/relationship/${relationshipType}/${relationshipId}/summary`}
      target={getLinkTarget(newTab)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {relationshipId}
    </Link>
  )
}

export function TreeLink({ treeId, treeName, treeType, ...props }) {
  const { t } = useTranslation()
  let linkText
  if (isNullOrUndefinedOrEmpty(treeId)) {
    return null
  }
  if (treeName && treeType) {
    linkText = `${treeId} (${treeName} • ${t(`tree.type.${treeType}`, treeType)})`
  } else if (treeName) {
    linkText = `${treeId} (${treeName})`
  } else {
    linkText = treeId
  }
  return (
    <Link
      id="Tree Link"
      to={`${getFrontendBaseUrl(false)}/tree/${treeId}/summary`}
      rel="noreferrer"
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {linkText}
    </Link>
  )
}

RelationshipLink.propTypes = {
  relationshipId: PropTypes.string,
  relationshipType: PropTypes.oneOf(['couple', 'parentchild', 'association']),
}

export function UserLink({ userId, userName, country, showId, newTab = true, ...props }) {
  let context = ''

  if (isNullOrUndefinedOrEmpty(userId) || userId === 'idNotFound') {
    return null
  }

  if (userId.includes('fs-ft.u.')) {
    userId = userId.split('.')[2]
  } else if (userId.includes('cis.user.')) {
    context = '?context=cisUserId'
  }

  let linkText = userName || userId
  if (userName && showId) {
    linkText = `${userName} (${userId})`
  }
  if (country != null) {
    linkText = `${linkText} - ${country}`
  }

  return (
    <Link
      id="User Link"
      to={`/user/${userId}/summary${context}`}
      target={getLinkTarget(newTab)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {linkText}
    </Link>
  )
}

UserLink.propTypes = {
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string,
}

export function MrnLink({ mrn, mrnText, newTab = true, forward = true, ...props }) {
  const linkText = !mrnText ? mrn : mrnText
  if (isNullOrUndefinedOrEmpty(mrn)) {
    return null
  }
  return (
    <Link
      to={`/user/${mrn}/summary?context=mrn&forward=${forward}`}
      target={getLinkTarget(newTab)}
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {linkText}
    </Link>
  )
}

export function CmisLink({ cmisId, newTab = true, forward = true, ...props }) {
  return (
    <Link
      to={`/user/${cmisId}/summary?context=cmisId&forward=${forward}`}
      target={getLinkTarget(newTab)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {cmisId}
    </Link>
  )
}

export function RanLink({ ranId, pid, newTab = true, ...props }) {
  const { error } = useNotifications()
  const [reservationPids, setReservationPids] = useState(pid ? [pid] : [])

  useEffect(() => {
    async function fetchReservationPersonIds() {
      try {
        const reservationPersonIdsResponse = await getReservationPersonIds(ranId)
        const reservationPersonIds = reservationPersonIdsResponse.data
        if (reservationPersonIds && reservationPersonIds.length > 0) {
          setReservationPids(reservationPersonIds)
        }
      } catch (err) {
        // error(`Error getting reservation person IDs: ${err.message || ''}`, err)
      }
    }
    if (!pid) {
      fetchReservationPersonIds()
    }
  }, [error, pid, ranId])

  if (reservationPids < 1) return ranId
  return (
    <Link
      id="RAN ID Link"
      to={`/person/${reservationPids[0]}/reservations?ranId=${ranId}`}
      target={getLinkTarget(newTab)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {ranId}
    </Link>
  )
}

export function CisLink({ userId, userName, text, ...props }) {
  let linkText = text || ''
  if (linkText === '') {
    if (userId && userName) {
      linkText = `${userName} (${userId})`
    }
    if (userId && !userName) {
      linkText = userId
    }
    if (!userId && userName) {
      return userName
    }
  }
  return (
    <Link
      id="CIS Link"
      to={`/user/${userId}/summary?context=cisUserId`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {linkText}
    </Link>
  )
}

export function MePersonLink({ cisId, userName, personId, personName, text, ...props }) {
  let result = null
  if (cisId) {
    const id = getIdElement(cisId)
    result = (
      <CisLink
        userId={id}
        userName={userName}
        text={text}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        props={props}
      />
    )
  }
  if (personId) {
    const id = getIdElement(personId)
    result = (
      <PersonLink
        personId={id}
        personName={personName}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        props={props}
      />
    )
  }
  return result
}

export function CtrLink({ ctrId, hidden, newTab = true, ...props }) {
  return (
    <span css={hidden && hiddenCss}>
      <Link
        id="ctrLink"
        to={`/temple/ctr/${ctrId}/summary`}
        target={getLinkTarget(newTab)}
        noScrollTop
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        {...props}
      >
        {ctrId}
      </Link>
    </span>
  )
}

CtrLink.propTypes = {
  ctrId: PropTypes.string.isRequired,
}

export function OwsLink({ owsId, ...props }) {
  return (
    <Link
      id="OWS Link"
      to={`/temple/ows/${owsId}/summary`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {owsId}
    </Link>
  )
}

OwsLink.propTypes = {
  owsId: PropTypes.string,
}

export function OsidLink({ osid, ...props }) {
  return (
    <Link
      id="OSID Link"
      to={`/temple/ows/${osid}/summary?idType=osid`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {osid}
    </Link>
  )
}

OsidLink.propTypes = {
  osid: PropTypes.string,
}

export function RequestLink({ requestId, ...props }) {
  return (
    <Link
      to={`/temple/request/${requestId}`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {requestId}
    </Link>
  )
}

RequestLink.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export function GroupLink({ groupId, groupName, text }) {
  return (
    <Link
      id="Group Link"
      to={`/group/${groupId}/summary`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {text || groupName || groupId}
    </Link>
  )
}

GroupLink.propTypes = {
  groupId: PropTypes.string,
}

export function UnitLink({ unitnumber }) {
  return (
    <Link
      to={`/unit/${unitnumber}`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {unitnumber}
    </Link>
  )
}

GroupLink.propTypes = {
  groupId: PropTypes.string,
}

export function ChatLink({ channelId, text, messageId, ...props }) {
  return (
    <Link
      id="Chat Link"
      to={`/chat/${channelId}/${messageId ? 'messages' : 'summary'}${messageId ? `?messageId=${messageId}` : ''}`}
      target="_blank"
      noScrollTop
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {text}
    </Link>
  )
}

export function ArtifactLink({ artifactId, children, ...props }) {
  return (
    <Link
      id="Artifact Link"
      to={`/artifacts/artifact/${artifactId}`}
      noScrollTop
      target="_blank"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...props}
    >
      {children || artifactId}
    </Link>
  )
}

export function DiscussionLink({ uri }) {
  const paramOption = uri.startsWith('ds.disc.') ? 'discussionId' : 'sourceId'
  return (
    <Link
      id={`Discussion Link - ${uri}`}
      to={`${getFrontendBaseUrl(true)}/tree/sources/admin?${paramOption}=${uri}`}
      target="_blank"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {uri}
    </Link>
  )
}
